import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { CalendarDays, Users, Scissors, Wallet, Receipt, FileText, LogOut, Menu, X, Image, DatabaseBackup } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/app/agenda", label: "Agenda", icon: CalendarDays, testId: "nav-agenda" },
  { to: "/app/clientes", label: "Clientes", icon: Users, testId: "nav-clientes" },
  { to: "/app/servicos", label: "Serviços", icon: Scissors, testId: "nav-servicos" },
  { to: "/app/certificados", label: "Certificados", icon: Image, testId: "nav-certificados" },
  { to: "/app/receber", label: "Receber", icon: Wallet, testId: "nav-receber" },
  { to: "/app/pagar", label: "Pagar", icon: Receipt, testId: "nav-pagar" },
  { to: "/app/relatorios", label: "Relatórios", icon: FileText, testId: "nav-relatorios" },
  { to: "/app/backup", label: "Backup", icon: DatabaseBackup, testId: "nav-backup" },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const todayLabel = new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#FAF9F5] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#121212] text-stone-300 flex flex-col transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
          <Logo size={32} light />
          <span className="font-display text-lg text-white leading-none">Marcio Pissaia</span>
        </div>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors duration-200 ${
                  isActive ? "bg-[#C8A279] text-[#121212] font-semibold" : "hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <p className="text-xs text-stone-400 mb-3 truncate" data-testid="admin-user-email">{user?.email}</p>
          <button
            onClick={doLogout}
            data-testid="admin-logout-button"
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-stone-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut size={15} /> Sair do sistema
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 shrink-0 bg-white border-b border-stone-200 flex items-center justify-between px-4 sm:px-8">
          <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="admin-menu-button" aria-label="Abrir menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <p className="text-sm text-stone-500 capitalize hidden sm:block" data-testid="admin-current-date">{todayLabel}</p>
          <p className="font-display text-lg lg:hidden">Painel</p>
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-400">Painel de Gestão</p>
        </header>
        <main className="flex-1 p-4 sm:p-8 min-w-0">{children}</main>
      </div>
    </div>
  );
}
